import type { League } from "../types/nba";

/* ==================================================================
 * LEAGUE REGISTRY — the seam between the shared shell and each sport.
 * Adding a league = a row here + a desk on the frontend + a provider on
 * the backend.
 * ================================================================== */
export interface LeagueEntry extends League {
  /** The switcher's second line. */
  sport: string;
  /** Where choosing this league takes you. */
  home: string;
  /** The header chip's label on the smallest phones, when the full one is too long (B-058). */
  short?: string;
  /** The header search button's accessible name: what that league's search dialog finds (Q675-02). */
  searchLabel: string;
}

/**
 * The dot colours are not here but in tokens.css (--nba-*, --ufc-*): they
 * differ per theme, and NBA's is two colours. `accent` stays the brand's own
 * primary for anything that needs one hex.
 *
 * A league with `enabled: false` is listed in the switcher as "Soon" — shown
 * so the roadmap is visible, reachable by keyboard so it can be read, and
 * inert when chosen.
 */
export const LEAGUES: LeagueEntry[] = [
  { key: "nba", label: "NBA", sport: "Basketball", home: "/", accent: "#1D428A", periodSeconds: 720, periods: 4, enabled: true, searchLabel: "Search players, teams, fighters and pages" },
  { key: "ufc", label: "UFC", sport: "MMA", home: "/ufc", accent: "#6B0F14", periodSeconds: 300, periods: 3, enabled: true, searchLabel: "Search fighters, players, teams and pages" },
  // The EuroLeague (DSPRT-451): four quarters of ten minutes. The dot is the league's orange (shell.css).
  { key: "euroleague", label: "EuroLeague", sport: "Basketball · Europe", home: "/euroleague", short: "EL", accent: "#F08A24", periodSeconds: 600, periods: 4, enabled: true, searchLabel: "Search EuroLeague players and clubs, NBA players and pages" },
  // The G League (DSPRT-474): the NBA's minor league, on the NBA desk in league mode. The dot is a ring in the NBA's two colours (shell.css).
  { key: "gleague", label: "G League", sport: "Basketball · NBA affiliates", home: "/gleague", short: "GL", accent: "#1D428A", periodSeconds: 720, periods: 4, enabled: true, searchLabel: "Search players, teams, fighters and pages" },
];

/**
 * Roadmap copy for the footer.
 *
 * This used to be a disabled "+" button beside the league chip — a control in
 * the most prominent slot on the page whose only function was to announce that
 * it did nothing. A disabled button is also unreachable by keyboard, so its
 * title never surfaced for the people most likely to go looking. Stated as a
 * sentence at the bottom it reads as a roadmap instead of a broken affordance.
 */
export const ROADMAP = "More leagues coming soon.";
